import { InvoiceDeliveryMethodIntegerEnum } from 'src/shared/models/enums/invoiceDeliveryMethodInteger.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Alert, AlertTitle, Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useGlobals } from 'src/shared/contexts/appContext';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useEffect } from 'react';
import { getInvoiceTemplateById, sendSendingsByMissionId } from 'src/shared/services/invoiceService';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { SendSendingsByMissionIdRequest } from '../models/sendSendingsByMissionIdRequest';

type SendMemberInvoiceKivraProps = {
  invoiceMission: InvoiceMissionResponse;
  sendingType: InvoiceSendingTypeIntegerEnum;
  navigateToInvoiceMission?: boolean;
  totalKivraMembers: number;
  onClose: () => void;
  loadingCallback: (loading: boolean) => void;
};

type FormValues = {
  subject?: string;
  body?: string;
  missionId: number;
  sndDate: string;
  deliveryMethod: InvoiceDeliveryMethodIntegerEnum;
  runAsTaskWithTaskService: boolean;
};

export const SendMemberInvoiceKivra = (props: SendMemberInvoiceKivraProps) => {
  const { invoiceMission, sendingType, navigateToInvoiceMission, totalKivraMembers, onClose, loadingCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { setDateStamp, currentUser } = useGlobals();
  const navigate = useNavigate();

  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const countNoKivra = invoiceMission.data.totalCount - totalKivraMembers;

  const InvoiceMailSenderSchema: yup.Schema<FormValues> = yup.object().shape({
    subject: yup.string().optional(),
    body: yup.string().optional(),
    sndDate: yup.string().required(),
    missionId: yup.number().required(),
    deliveryMethod: yup.number().required(),
    runAsTaskWithTaskService: yup.boolean().required(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(InvoiceMailSenderSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      body: '',
      sndDate: DateHelper.getDate(new Date()),
      missionId: invoiceMission.invoiceMission_id,
      deliveryMethod: InvoiceDeliveryMethodIntegerEnum.kivra,
      runAsTaskWithTaskService: false,
    },
  });

  const { handleSubmit, setValue } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      loadingCallback(true);
      try {
        const res = await getInvoiceTemplateById(invoiceMission.template_id ?? 0);
        if (isMounted()) {
          ConsoleHelper.log('res?.data TEMPLATE', res?.data);
          const sendingContent = res?.data.sendings?.find((sendingItem) => sendingItem.sendingType === InvoiceSendingTypeIntegerEnum.Email);
          ConsoleHelper.log('sendingContent', sendingContent);
          setValue('subject', sendingContent?.emailHeading);
          setValue('body', sendingContent?.emailText);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) loadingCallback(false);
      }
    };

    fetchData();
  }, [isMounted, invoiceMission, setValue, loadingCallback]);

  const onSubmit = async (data: FormValues) => {
    loadingCallback(true);
    const dataToPost: SendSendingsByMissionIdRequest = {
      missionId: data.missionId,
      sndDate: data.sndDate,
      deliveryMethod: data.deliveryMethod,
      async: true,
      sendingType,
      runAsTaskWithTaskService: data.runAsTaskWithTaskService,
    };
    ConsoleHelper.log('dataToPost', dataToPost);
    try {
      await sendSendingsByMissionId(dataToPost);
      if (isMounted()) {
        setDateStamp(new Date());
        toast.success(<>{t('invoice:createMemberInvoice.invoicesSending')}</>);
        if (navigateToInvoiceMission) {
          navigate(`${RoutesEnum.InvoiceMissionDetailSendingType}/${data.missionId}/${InvoiceSendingTypeIntegerEnum.NotSet}`);
        }
        onClose();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:invoiceMissionPage.failedSending')}</>);
      }
    } finally {
      loadingCallback(false);
    }
  };

  const getEmailAlertMessage = () => {
    if (totalKivraMembers === 0) {
      return t('invoice:invoiceMissionPage.kivra.alertAllMissingKirva');
    }
    if (totalKivraMembers === invoiceMission.data.totalCount) {
      return t('invoice:invoiceMissionPage.kivra.alertAllHaveKirva');
    }
    return t('invoice:invoiceMissionPage.kivra.alertText', { countNoKivra });
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form id='invoiceForm' onSubmit={handleSubmit(onSubmit)}>
          <GridWrapper>
            <Grid item xs={12}>
              <Alert severity='info'>
                <AlertTitle>
                  {t('invoice:invoiceMissionPage.kivra.alertTitle', {
                    kivraMemberCount: totalKivraMembers,
                    totaltInvoicesCount: invoiceMission.data.totalCount,
                  })}
                </AlertTitle>
                {getEmailAlertMessage()}
                <br />

                {t('invoice:invoiceMissionPage.kivra.alertAttachmentInfo')}
              </Alert>
            </Grid>
          </GridWrapper>
          <GridWrapper>
            <Grid item xs={12} mt={1.5}>
              <Box sx={{ p: 2 }}>
                {isSuperAdmin && (
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='runAsTaskWithTaskService' label={t('invoice:runAsTask')} />
                  </Grid>
                )}
                <GridWrapper>
                  <Grid item xs={12} mt={1.5}>
                    <ControlledTextField name='subject' label={t('invoice:createInvoice.invoiceRow.heading')} disabled />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextEditor rows={30} name='body' disabled />
                  </Grid>
                </GridWrapper>
              </Box>
            </Grid>
          </GridWrapper>
        </form>
      </FormProvider>
    </>
  );
};
